// react
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';

// material ui
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Fade,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import { AddButton } from '@/components/ui/button';
import SingleDateSelector from '@/components/ui/datetime/SingleDateRangeSelector';
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// feature-components
import AnalysisAggregateMenu from '@/features/energyxplorer/components/analysis/AnalysisAggregateMenu';
import { AnalysisViewToggleButtonGroup } from '@/features/energyxplorer/components/analysis/AnalysisViewToggleButtonGroup';
import EnergyAnalysisComparisonSeriesChart from '@/features/energyxplorer/components/analysis/EnergyAnalysisComparisonSeriesChart';
import EnergyAnalyzerFilterInput from '@/features/energyxplorer/components/analysis/EnergyAnalyzerFilterInput';
import { TimeAggregateButtonGroup } from '@/features/energyxplorer/components/analysis/TimeAggregateButtonGroup';
import HighlightControls from '@/features/energyxplorer/components/HighlightControls';
import ComparisonItemSelector from '@/features/energyxplorer/components/nodes/ComparisonItemSelector';
import EnergyTreeNodeTraversal from '@/features/energyxplorer/components/nodes/EnergyTreeNodeTraversal';

// feature-hooks
import { useAggregationParam } from '@/features/energyxplorer/hooks/useAggregationParam';
import useChartHighlightParams from '@/features/energyxplorer/hooks/useChartHighlightParams';
// * Hooks
import { useEnergyTreeNode } from '@/features/energyxplorer/hooks/useEnergyTreeNode';
import { useTimeAggregate } from '@/features/energyxplorer/hooks/useTimeAggregate';
import useUrlFilters from '@/features/energyxplorer/hooks/useUrlFilters';
import useUrlViewMode from '@/features/energyxplorer/hooks/useUrlViewMode';

// components
import { useUrlComparisonDateRange } from '@/hooks/useUrlComparisonDateRange';
import useUrlComparisonNodes from '@/hooks/useUrlComparisonNodes';
import { useUrlDateRange } from '@/hooks/useUrlDateRange';

// relative
import { useLazyGetEnergyConsumptionQuery } from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice';
import EnergyTreeNodeAnalysisToolbar from './EnergyTreeNodeAnalysisToolbar';

import { DateTime } from 'luxon';

function shouldSkipQuery({ filters, selectedEnergyGroup, energyTreeNode }) {
  if (!energyTreeNode || !selectedEnergyGroup) return true;

  const hasActivePeriodFilter =
    filters &&
    Object.prototype.hasOwnProperty.call(filters, 'activePeriodFilter');

  const isPortfolio = selectedEnergyGroup?.type?.includes('Portfolio') === true;

  const hasOpeningHours = !!energyTreeNode?.options?.openingHours;

  // We SKIP when portfolio nodes lack opening hours AND no activeDaysFilter
  return isPortfolio && !hasOpeningHours && hasActivePeriodFilter;
}

/**
 * formatDate - Formats a Luxon DateTime object to a string in the format "d. MMMM"
 * @param {DateTime} date - The Luxon DateTime object to format
 */
const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  return `${day} ${month}`;
};

const EnergyTreeNodeComparisonAnalysisPage = () => {
  const { t } = useTranslation([
    'energyxplorer-analysis-tabs',
    'energyxplorer',
  ]);

  const { timeRange } = useUrlDateRange();
  const { comparisonTimeRange } = useUrlComparisonDateRange();
  const { accountId, energyGroupId } = useParams({ strict: false });
  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    { skip: !energyGroupId },
  );
  const {
    energyTreeNode,
    setNodeId,
    isFetching: isFetchingEnergyTreeNode,
  } = useEnergyTreeNode();

  const {
    comparisonNodeIds,
    addComparisonNode,
    removeComparisonNode,
    updateComparisonNode,
  } = useUrlComparisonNodes();

  // * aggregation in URL, sum, mean, min, max
  const { aggregation } = useAggregationParam('aggregation');

  const { filters: comparisonFilters, applyFilters } = useUrlFilters({}); // default filters here if you have them

  const { highlightEnabled, highlightRange, toggleHighlight, clearHighlight } =
    useChartHighlightParams();

  //===API====//
  const [
    getEnergyConsumptionData,
    {
      isLoading: isLoadingEnergyConsumptionData,
      isFetching: isFetchingEnergyConsumptionData,
    },
  ] = useLazyGetEnergyConsumptionQuery();

  //===State====//

  const [filtersOpen, setFiltersOpen] = useState(true);

  // Derive comparisonItems from URL-based node IDs
  const comparisonItems = useMemo(() => {
    if (!selectedEnergyGroup?.energyTreeNodes) return [];

    return comparisonNodeIds.map((nodeId) => {
      const node = selectedEnergyGroup.energyTreeNodes.find(
        (n) => n.energyTreeNodeId === nodeId,
      );
      return { node: node || null };
    });
  }, [comparisonNodeIds, selectedEnergyGroup]);

  const [aggregate] = useTimeAggregate();
  // view mode in URL
  const [viewMode] = useUrlViewMode({
    allowed: ['column', 'line'],
    fallback: 'column',
  });

  const selectedRange = [
    DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
    DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
  ];

  const [data, setData] = useState(null);
  const [comparisonEnergyConsumptionData, setComparisonEnergyConsumptionData] =
    useState([]); // For comparison data

  const isComparingSameNode = useMemo(
    () =>
      comparisonItems.some(
        (item) =>
          item?.node?.energyTreeNodeId === energyTreeNode?.energyTreeNodeId,
      ),
    [comparisonItems, energyTreeNode],
  );

  const dataSeriesAggregate = { value: aggregation };

  const fetchEnergyConsumption = useCallback(
    async ({ nodeId, start, end, filters, aggregate }) => {
      try {
        const response = await getEnergyConsumptionData({
          accountId: accountId,
          energyGroupId: selectedEnergyGroup?.energyGroupId,
          nodeId,
          start,
          end,
          aggregate,
          ...filters,
        }).unwrap();

        return {
          ...response,
          metadata: {
            ...filters,
            id: Date.now(),
          },
        };
      } catch {
        // Error handled by apiErrorMiddleware (snackbar + App Insights).
        return null;
      }
    },
    [getEnergyConsumptionData, accountId, selectedEnergyGroup],
  );

  const handleApplyFilters = async (filters) => {
    applyFilters(filters); // merges + syncs to URL
  };

  const addComparisonItem = useCallback(() => {
    // Add a null/empty node ID to create a new comparison slot
    addComparisonNode(null);
  }, [addComparisonNode]);

  const removeComparisonItem = useCallback(
    (index) => {
      removeComparisonNode(index);
    },
    [removeComparisonNode],
  );

  const updateComparisonItem = useCallback(
    (index, key, value) => {
      // Only handle 'node' key updates (value will be the node object)
      if (key === 'node' && value) {
        updateComparisonNode(index, value.energyTreeNodeId);
      } else if (key === 'node' && !value) {
        // If clearing the node, set to null
        updateComparisonNode(index, null);
      }
    },
    [updateComparisonNode],
  );

  useEffect(() => {
    if (!energyTreeNode || !timeRange || !aggregate || !comparisonFilters)
      return;

    const fetchNodeToAnalyseData = async () => {
      const shouldSkip = shouldSkipQuery({
        filters: comparisonFilters,
        selectedEnergyGroup,
        energyTreeNode,
      });
      if (shouldSkip) {
        setData(null);
        return;
      }
      const result = await fetchEnergyConsumption({
        nodeId: energyTreeNode.energyTreeNodeId,
        start: timeRange.start,
        end: timeRange.end,
        filters: comparisonFilters,
        aggregate,
      });

      if (result && result !== null) {
        setData(result);
      } else {
        setData(null);
      }
    };

    fetchNodeToAnalyseData();
  }, [
    energyTreeNode,
    timeRange,
    aggregate,
    viewMode,
    comparisonFilters,
    fetchEnergyConsumption,
    selectedEnergyGroup,
  ]);

  useEffect(() => {
    const fetchAllComparisonItems = async () => {
      const newComparisonData = await Promise.all(
        comparisonItems.map(async (item) => {
          const shouldSkip = shouldSkipQuery({
            filters: comparisonFilters,
            selectedEnergyGroup,
            energyTreeNode: item.node,
          });
          if (shouldSkip || !item.node) return null;

          const result = await fetchEnergyConsumption({
            nodeId: item.node?.energyTreeNodeId,
            start: comparisonTimeRange.start,
            end: comparisonTimeRange.end,
            filters: comparisonFilters, // from your filter state
            aggregate,
            metadataId: item.id, // optional, to track it
          });

          return result;
        }),
      );

      setComparisonEnergyConsumptionData(newComparisonData.filter(Boolean));
    };

    fetchAllComparisonItems();
  }, [
    comparisonItems,
    comparisonFilters,
    comparisonTimeRange,
    aggregate,
    viewMode,
    fetchEnergyConsumption,
    selectedEnergyGroup,
  ]); // only runs when items are added/removed

  useEffect(() => {
    if (
      !energyTreeNode &&
      selectedEnergyGroup &&
      isFetchingEnergyTreeNode === false
    ) {
      setNodeId(selectedEnergyGroup?.energyTreeNodes[0]?.energyTreeNodeId); // Set first available node as default
    }
  }, [
    energyTreeNode,
    isFetchingEnergyTreeNode,
    selectedEnergyGroup,
    setNodeId,
  ]);

  const shouldSkip = shouldSkipQuery({
    filters: comparisonFilters,
    selectedEnergyGroup,
    energyTreeNode,
  });

  const readyToRender =
    selectedEnergyGroup && energyTreeNode && comparisonFilters;

  return (
    <PageContainer sx={{ pt: 0 }}>
      {readyToRender && (
        <Fade in={readyToRender} timeout={500}>
          <Grid container spacing={2}>
            <Grid
              sx={{
                transition: 'flex-basis 0.3s ease-in-out',
              }}
              size={{
                md: 12,
                lg: filtersOpen ? 9 : 12
              }}>
              <Card variant="outlined">
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" gap={1}>
                      {energyTreeNode?.name}
                      <InfoTooltipIcon
                        title={t('display_sum_of_consumption_over_time')}
                      />
                    </Box>
                  }
                  subheader={
                    selectedRange[0] && selectedRange[1]
                      ? `${t('comparison_analysis.in_period')} ${formatDate(selectedRange[0])} - ${formatDate(selectedRange[1])}`
                      : t('comparison_analysis.select_period')
                  }
                  action={
                    <EnergyTreeNodeAnalysisToolbar
                      showFilterToggle={true}
                      filtersOpen={filtersOpen}
                      onToggleFilters={() => setFiltersOpen(!filtersOpen)}
                      actions={
                        <Stack direction="row" gap={2} alignItems="center">
                          <HighlightControls
                            highlightEnabled={highlightEnabled}
                            hasHighlights={!!highlightRange}
                            onToggle={toggleHighlight}
                            onClear={clearHighlight}
                          />
                          <AnalysisAggregateMenu />

                          <AnalysisViewToggleButtonGroup />

                          <TimeAggregateButtonGroup />
                        </Stack>
                      }
                    />
                  }
                />
                <CardContent>
                  <EnergyAnalysisComparisonSeriesChart // Comparison Series Chart to compare data accross multiple nodes
                    energyConsumptionData={data}
                    dataSeriesAggregate={dataSeriesAggregate}
                    comparisonEnergyConsumptionData={
                      comparisonEnergyConsumptionData
                    }
                    node={energyTreeNode}
                    isLoading={
                      isLoadingEnergyConsumptionData ||
                      isFetchingEnergyConsumptionData
                    }
                    viewMode={viewMode}
                    aggregate={aggregate}
                    selectedEnergyGroup={selectedEnergyGroup}
                    comparisonItems={comparisonItems}
                    comparisonFilters={comparisonFilters}
                    querySkipped={shouldSkip}
                  />
                </CardContent>
              </Card>
            </Grid>

            {filtersOpen && (
              <Grid
                className="non-printable"
                sx={{
                  transition: 'flex-basis 0.3s ease', // smooth collapse/expand
                }}
                size={{
                  md: 12,
                  lg: 3
                }}>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            variant="body1"
                            color="textSecondary"
                            fontWeight="bold"
                          >
                            {t('comparison_analysis.filtering')}
                          </Typography>
                          <Tooltip
                            title={t('comparison_analysis.use_filters')}
                            placement="right"
                          >
                            <InfoRoundedIcon
                              fontSize="small"
                              sx={{
                                color: (theme) =>
                                  theme.palette.mode === 'light'
                                    ? 'lightgray'
                                    : 'gray',
                                ml: 1,
                              }}
                            />
                          </Tooltip>
                        </Box>
                        <Suspense
                          fallback={
                            <div>
                              {t('analysis.loading', { ns: 'energyxplorer' })}
                            </div>
                          }
                        >
                          <EnergyAnalyzerFilterInput
                            scope="comparison"
                            handleApply={handleApplyFilters}
                            selectedEnergyGroup={selectedEnergyGroup}
                          />
                        </Suspense>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid size={12}>
                    <EnergyTreeNodeTraversal displayChildNodeList={false} />
                  </Grid>
                  <Grid size={12}>
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ mt: 1, mb: 1 }}
                        >
                          {t('comparison_analysis.compare_consumption_with')}{' '}
                          {energyTreeNode?.name}{' '}
                          {t(
                            'comparison_analysis.and_other_measurement_points',
                          )}
                        </Typography>
                        <Grid
                          container
                          alignItems="center"
                          spacing={2}
                          sx={{ mb: 2 }}
                        >
                          <Grid size={12}>
                            <Suspense
                              fallback={
                                <div>
                                  {t('analysis.loading', {
                                    ns: 'energyxplorer',
                                  })}
                                </div>
                              }
                            >
                              <SingleDateSelector
                                isComparingSameNode={isComparingSameNode}
                                comparisonItems={comparisonItems}
                                aggregate={aggregate}
                              />
                            </Suspense>
                          </Grid>

                          <Grid size="grow">
                            <AddButton
                              onClick={addComparisonItem}
                              fullWidth
                              startIcon={<AddRoundedIcon />}
                              disabled={comparisonItems
                                .slice(0)
                                .some((item) => item.node === null)}
                            >
                              {t('comparison_analysis.add_measurement_point')}
                            </AddButton>
                          </Grid>
                        </Grid>

                        {comparisonItems.map((item, index) => (
                          <React.Fragment key={index}>
                            <Grid
                              container
                              alignItems="center"
                              spacing={2}
                              sx={{ mb: 1 }}
                            >
                              <Grid size="grow">
                                <ComparisonItemSelector
                                  nodeOptions={
                                    selectedEnergyGroup?.energyTreeNodes
                                  }
                                  selectedNode={item.node}
                                  onChange={(newValue) =>
                                    updateComparisonItem(
                                      index,
                                      'node',
                                      newValue,
                                    )
                                  }
                                  onRemove={() => removeComparisonItem(index)}
                                  showRemoveButton={true}
                                  placeholder={
                                    t('common.select_node_for_comparison') ||
                                    'Vælg sammenligningsnode'
                                  }
                                />
                              </Grid>
                            </Grid>
                          </React.Fragment>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Fade>
      )}
    </PageContainer>
  );
};

export default EnergyTreeNodeComparisonAnalysisPage;
